import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { color, l, Popover } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { getPricingLimitInfo } from 'common/billing/entities/pro/selectors'
import { fetchPricingLimitInfo } from 'common/settings/actions'

import CircleProgress from './CircularProgress'

import cm from './ContactsLimit.module.css'

interface ContactsLimitProps {
  isSidebarHidden: boolean
  fbPagesOpen: boolean
  isRightTimeAnimate: boolean
}

const ContactsLimit = ({
  isSidebarHidden,
  fbPagesOpen,
  isRightTimeAnimate,
}: ContactsLimitProps) => {
  const [isFolded, setFolded] = useState(isSidebarHidden && !fbPagesOpen)
  const [isUnfolded, setUnfolded] = useState(!isFolded && isRightTimeAnimate)
  const [open, setOpen] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const updateFolded = useCallback(() => {
    const state = isSidebarHidden && !fbPagesOpen
    setFolded(state)

    if (!(fbPagesOpen && isUnfolded)) {
      setUnfolded(!state && isRightTimeAnimate)
    }
  }, [isSidebarHidden, fbPagesOpen, isRightTimeAnimate, isUnfolded])

  useEffect(() => {
    updateFolded()
  }, [isSidebarHidden, isRightTimeAnimate, updateFolded])

  useEffect(() => {
    // Emulate isRightTimeAnimate from sidebar
    if (fbPagesOpen) {
      updateFolded()
      setTimeout(() => {
        setUnfolded(true)
      }, 300)
    }
  }, [fbPagesOpen, updateFolded])

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const {
    subscribers_total: subscribersTotal,
    subscribers_limit: subscribersLimit,
    MIN_LIMIT_TO_BLOCK,
  } = useAppSelector(getPricingLimitInfo)

  let activeColor: string
  const warningLimit = subscribersLimit - MIN_LIMIT_TO_BLOCK
  if (subscribersTotal < warningLimit) {
    activeColor = color.icon.success
  } else if (subscribersTotal <= subscribersLimit) {
    activeColor = color.icon.warning
  } else {
    activeColor = color.icon.danger
  }

  // Render minimal visible progress
  const total = subscribersTotal > 0 ? Math.max(subscribersTotal, subscribersLimit / 20) : 0

  const progress = (total / subscribersLimit) * 100

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchPricingLimitInfo())
  }, [dispatch])

  const handleEnter = () => {
    if (!isFolded) {
      return
    }

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
    }
    setOpen(true)
  }

  const handleLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }

  const renderDescription = (isFolded: boolean) => (
    <div className={cx(cm.description, { 'p-a-xs': isFolded })}>
      <div className={cm.title}>{l.translate('Free contacts limit')}</div>
      <div className="d-flex align-center">
        {subscribersTotal}/{subscribersLimit}{' '}
      </div>
    </div>
  )

  return (
    <div className="d-flex p-y">
      <div
        ref={anchorRef}
        className={cm.progress}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <CircleProgress size={30} progress={progress} strokeWidth={5} activeColor={activeColor} />
      </div>

      <Popover
        open={open}
        anchor={anchorRef.current}
        at="right"
        arrow
        useLayerForClickAway={false}
        onRequestClose={() => setOpen(false)}
      >
        <div onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
          {renderDescription(isFolded)}
        </div>
      </Popover>

      {isUnfolded && renderDescription(isFolded)}
    </div>
  )
}

export default ContactsLimit
